/** Cursor-style human presentation for tool calls (chat + work-log titles). */

export interface ToolField {
  key: string;
  label: string;
  value: string;
}

export interface ToolPresentation {
  /** Collapsed one-liner, e.g. "Read integrations.component.ts" */
  summary: string;
  /** Short verb for iconography / grouping, e.g. "Read" */
  verb: string;
  /** PrimeIcons class */
  icon: string;
  /** User-meaningful fields only */
  keyFields: ToolField[];
  /** Basename / short target when applicable */
  target?: string;
}

const HIDDEN_FIELDS = new Set([
  'workspaceId',
  'actionKey',
  'input',
  'owner',
  'repo',
  'content',
  'oldString',
  'newString',
  'edits',
  'body',
]);

const FIELD_LABELS: Record<string, string> = {
  path: 'Path',
  command: 'Command',
  query: 'Query',
  pattern: 'Pattern',
  startLine: 'Start line',
  endLine: 'End line',
  recursive: 'Recursive',
  branch: 'Branch',
  message: 'Message',
  state: 'State',
  limit: 'Limit',
  title: 'Title',
  pullNumber: 'Pull request',
};

const PROVIDER_LABELS: Record<string, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  slack: 'Slack',
  gmail: 'Gmail',
  jira: 'Jira',
  notion: 'Notion',
  linear: 'Linear',
  http: 'HTTP',
  sql: 'SQL',
};

/** Turns safe tool keys (`fs_read_file`, `integration_github_call`) into readable labels. */
export function humanizeToolName(name: string): string {
  const spaced = name
    .replace(/[_.]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : 'Tool';
}

function basename(path: string): string {
  const cleaned = path.replace(/\\/g, '/');
  const parts = cleaned.split('/').filter(Boolean);
  return parts[parts.length - 1] || path;
}

function asRecord(input: unknown): Record<string, unknown> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  return input as Record<string, unknown>;
}

/** Unwrap catalog-style `{ actionKey, input }` envelopes. */
function unwrapParams(toolName: string, input: unknown): {
  params: Record<string, unknown>;
  actionKey?: string;
  provider?: string;
} {
  const raw = asRecord(input);
  const catalog = toolName.match(/^integration_i_([0-9a-f]+)_(call|discover|describe)$/);
  if (catalog) {
    const nested = raw['input'];
    const params =
      nested && typeof nested === 'object' && !Array.isArray(nested)
        ? (nested as Record<string, unknown>)
        : raw;
    const actionKey = typeof raw['actionKey'] === 'string' ? raw['actionKey'] : catalog[2];
    return { params, actionKey, provider: decodeHex(catalog[1]) };
  }

  if (toolName.startsWith('integration_') && !toolName.startsWith('integration_i_')) {
    const rest = toolName.slice('integration_'.length);
    const parsed = splitIntegration(rest);
    return { params: raw, actionKey: parsed.actionKey, provider: parsed.provider };
  }

  return { params: raw };
}

function decodeHex(hex: string): string {
  let out = '';
  for (let i = 0; i < hex.length; i += 2) {
    out += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
  }
  return out;
}

const KNOWN_ACTIONS = [
  'merge_pull_request',
  'create_pull_request',
  'list_pull_requests',
  'get_pull_request_diff',
  'get_pull_request',
  'add_pull_request_comment',
  'request_reviewers',
  'create_issue',
  'list_issues',
  'get_issue',
  'list_branches',
  'get_file',
  'get_tree',
  'create_branch',
  'commit_file',
  'get_repository',
  'create_or_update_file',
  'post_message',
  'list_channels',
  'send_email',
  'send_mail',
  'query',
  'request',
  'send',
];

function splitIntegration(rest: string): { provider: string; actionKey: string } {
  for (const action of KNOWN_ACTIONS) {
    if (rest === action || rest.endsWith(`_${action}`)) {
      const prefix = rest === action ? rest : rest.slice(0, -(action.length + 1));
      return { provider: prefix || action, actionKey: action };
    }
  }
  const idx = rest.lastIndexOf('_');
  if (idx <= 0) return { provider: rest, actionKey: rest };
  return { provider: rest.slice(0, idx), actionKey: rest.slice(idx + 1) };
}

function providerLabel(provider: string): string {
  const normalized = provider.split(/[.\-_]/)[0]?.toLowerCase() ?? '';
  if (PROVIDER_LABELS[normalized]) return PROVIDER_LABELS[normalized];
  for (const [key, label] of Object.entries(PROVIDER_LABELS)) {
    if (provider.toLowerCase().includes(key)) return label;
  }
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : 'Integration';
}

function providerIcon(provider: string, toolName: string): string {
  const normalized = provider.split(/[.\-_]/)[0]?.toLowerCase() ?? '';
  if (normalized === 'github' || toolName.includes('github')) return 'pi-github';
  if (normalized === 'gitlab') return 'pi-gitlab';
  if (normalized === 'slack') return 'pi-slack';
  if (toolName.startsWith('git.') || toolName.startsWith('git_')) return 'pi-git-branch';
  if (toolName.startsWith('fs_')) return 'pi-file';
  if (toolName.startsWith('shell_')) return 'pi-terminal';
  return 'pi-cog';
}

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function stringField(params: Record<string, unknown>, key: string): string | undefined {
  const value = params[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function truncate(text: string, max = 72): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function buildKeyFields(params: Record<string, unknown>, prefer: string[]): ToolField[] {
  const fields: ToolField[] = [];
  const seen = new Set<string>();

  for (const key of prefer) {
    if (HIDDEN_FIELDS.has(key) || seen.has(key)) continue;
    const value = params[key];
    if (value === undefined || value === null || value === '') continue;
    let display: string;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      display = String(value);
    } else {
      continue;
    }
    seen.add(key);
    fields.push({ key, label: fieldLabel(key), value: display });
  }

  for (const [key, value] of Object.entries(params)) {
    if (HIDDEN_FIELDS.has(key) || seen.has(key)) continue;
    if (value === undefined || value === null || value === '') continue;
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') continue;
    seen.add(key);
    fields.push({ key, label: fieldLabel(key), value: String(value) });
  }

  return fields;
}

/**
 * Turn a tool name + input into a Cursor-style one-line summary and key fields.
 */
export function summarizeToolCall(toolName: string, input: unknown): ToolPresentation {
  const { params, actionKey, provider } = unwrapParams(toolName, input);
  const path = stringField(params, 'path');
  const command = stringField(params, 'command');
  const query = stringField(params, 'query') ?? stringField(params, 'pattern');

  if (toolName === 'fs_read_file' || toolName.endsWith('_read_file')) {
    const target = path ? basename(path) : undefined;
    return {
      summary: target ? `Read ${target}` : 'Read file',
      verb: 'Read',
      icon: 'pi-file',
      target,
      keyFields: buildKeyFields(params, ['path', 'startLine', 'endLine']),
    };
  }

  if (toolName === 'fs_write_file') {
    const target = path ? basename(path) : undefined;
    return {
      summary: target ? `Wrote ${target}` : 'Wrote file',
      verb: 'Wrote',
      icon: 'pi-file-edit',
      target,
      keyFields: buildKeyFields(params, ['path']),
    };
  }

  if (toolName === 'fs_edit') {
    const target = path ? basename(path) : undefined;
    return {
      summary: target ? `Edited ${target}` : 'Edited file',
      verb: 'Edited',
      icon: 'pi-file-edit',
      target,
      keyFields: buildKeyFields(params, ['path']),
    };
  }

  if (toolName === 'fs_multi_edit') {
    const edits = Array.isArray(params['edits']) ? params['edits'] : [];
    const count = edits.length;
    return {
      summary: count === 1 ? 'Edited 1 file' : `Edited ${count || 0} files`,
      verb: 'Edited',
      icon: 'pi-file-edit',
      keyFields: [],
    };
  }

  if (toolName === 'fs_list_dir') {
    const target = path ?? '.';
    return {
      summary: `Listed ${target === '.' || target === '/' ? 'workspace' : basename(target)}`,
      verb: 'Listed',
      icon: 'pi-folder',
      target: basename(target),
      keyFields: buildKeyFields(params, ['path', 'recursive']),
    };
  }

  if (toolName === 'fs_grep' || toolName.includes('grep') || toolName.includes('search')) {
    return {
      summary: query ? `Searched ${truncate(query, 48)}` : 'Searched',
      verb: 'Searched',
      icon: 'pi-search',
      target: query,
      keyFields: buildKeyFields(params, ['query', 'pattern', 'path']),
    };
  }

  if (toolName.startsWith('shell_') || toolName === 'shell_run' || toolName === 'shell_test') {
    return {
      summary: command ? `Ran ${truncate(command)}` : 'Ran command',
      verb: 'Ran',
      icon: 'pi-terminal',
      target: command,
      keyFields: buildKeyFields(params, ['command']),
    };
  }

  if (toolName.startsWith('git.') || toolName.startsWith('git_')) {
    const action = humanizeToolName(toolName.replace(/^git[._]/, ''));
    const branch = stringField(params, 'branch');
    return {
      summary: branch ? `${action} ${branch}` : action,
      verb: action,
      icon: 'pi-git-branch',
      target: branch ?? path,
      keyFields: buildKeyFields(params, ['branch', 'path', 'message']),
    };
  }

  if (provider || toolName.startsWith('integration_')) {
    const label = provider ? providerLabel(provider) : 'Integration';
    const action = actionKey ? humanizeToolName(actionKey) : 'Action';
    const repo =
      stringField(params, 'owner') && stringField(params, 'repo')
        ? `${stringField(params, 'owner')}/${stringField(params, 'repo')}`
        : undefined;
    return {
      summary: `${label} · ${action}`,
      verb: action,
      icon: providerIcon(provider ?? '', toolName),
      target: repo,
      keyFields: [
        ...(repo ? [{ key: 'repo', label: 'Repository', value: repo }] : []),
        ...buildKeyFields(params, ['pullNumber', 'title', 'state', 'path', 'branch']),
      ],
    };
  }

  const label = humanizeToolName(toolName);
  const target = path ? basename(path) : command ? truncate(command, 40) : query ? truncate(query, 40) : undefined;
  return {
    summary: target ? `${label} ${target}` : label,
    verb: label,
    icon: providerIcon('', toolName),
    target,
    keyFields: buildKeyFields(params, ['path', 'command', 'query', 'pattern']),
  };
}

/**
 * Map a work-log `Tool call:` / `Tool result:` title to a Cursor-style summary
 * when the entry detail still carries an `Input:` JSON payload.
 */
export function friendlyWorkLogToolTitle(title: string, detail: string): string {
  const callMatch = title.match(/^Tool call:\s*(.+)$/i);
  const resultMatch = title.match(/^Tool result:\s*(.+)$/i);
  const toolName = (callMatch?.[1] ?? resultMatch?.[1])?.trim();
  if (!toolName) return title;

  let input: unknown;
  const labeled = detail.match(/^Input:\s*\n?([\s\S]*?)(?:\n\n[\s\S]*)?$/i);
  if (labeled) {
    try {
      input = JSON.parse(labeled[1].trim());
    } catch {
      input = undefined;
    }
  }

  return summarizeToolCall(toolName, input).summary;
}

/** Pretty-print unknown tool input/output for the optional “View raw” disclosure. */
export function formatToolRaw(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
